import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import logoPlaceholder from './assets/hamilton-services-logo-notext.png';
import {
  FiArrowRight,
  FiActivity,
  FiBarChart2,
  FiBriefcase,
  FiCrosshair,
  FiGrid,
  FiHelpCircle,
  FiInfo,
  FiLogIn,
  FiLogOut,
  FiMail,
  FiMenu,
  FiSettings,
  FiShield,
  FiTarget,
  FiTrendingUp,
  FiUser,
  FiX,
} from 'react-icons/fi';
import { FaCanadianMapleLeaf } from 'react-icons/fa';
import { RiScales3Line } from 'react-icons/ri';

// Theme
import { useThemeStore, applyTheme } from './components/ThemeContext';
import { LanguageProvider, useLanguage } from './components';

// Pages
import Dashboard from './pages/Dashboard';
import PortfolioGenerator from './pages/PortfolioGenerator';
import PortfolioBuilder from './pages/PortfolioBuilder';
import MarketData from './pages/MarketData';
import AdminPanel from './pages/AdminPanel';
import Login from './pages/Login';
import Mission from './pages/Mission';
import Contact from './pages/Contact';
import ChangePassword from './pages/ChangePassword';
import StockComparison from './pages/StockComparison';
import HelpDocs from './pages/HelpDocs';
import SharedPortfolio from './pages/SharedPortfolio';
import Watchlist from './pages/Watchlist';
import Alerts from './pages/Alerts';
import PerformanceDashboard from './pages/PerformanceDashboard';
import Goals from './pages/Goals';
import Landing from './pages/Landing';
import FinancialPlanningHub from './pages/FinancialPlanningHub';
import AccountsLearningCenter from './pages/AccountsLearningCenter';
import ConceptsLibrary from './pages/ConceptsLibrary';
import GrowthForecastCalculator from './pages/GrowthForecastCalculator';
import GoalPlanningEngine from './pages/GoalPlanningEngine';
import RetirementReadinessCenter from './pages/RetirementReadinessCenter';
import PlanningMission from './pages/PlanningMission';
import KeyboardShortcuts from './components/KeyboardShortcuts';
import OnboardingWizard from './components/OnboardingWizard';
import './consumerRedesign.css';

// API base URL strategy:
// - Production: explicit REACT_APP_API_URL (or same-origin /api reverse proxy)
// - Development: CRA proxy fallback
const IS_PRODUCTION = process.env.NODE_ENV === 'production';
const PROD_API_BASE = 'https://hamilton-services-backend.onrender.com/api';
const normalizeApiBase = (value) => String(value || '').trim().replace(/\/+$/, '');
const isLocalApiBase = (value) => /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/|$)/i.test(String(value || '').trim());
const ENV_API_BASE = normalizeApiBase(process.env.REACT_APP_API_URL);
const API_BASE = (() => {
  if (IS_PRODUCTION) {
    if (ENV_API_BASE && !isLocalApiBase(ENV_API_BASE)) {
      return ENV_API_BASE;
    }
    return PROD_API_BASE;
  }
  return ENV_API_BASE || '/api';
})();
const DEFAULT_API_TIMEOUT_MS = IS_PRODUCTION ? 45000 : 15000;
const LONG_RUNNING_API_TIMEOUT_MS = IS_PRODUCTION ? 60000 : 25000;
const API_NOTICE_EVENT = 'hamilton-api-notice';

const emitApiNotice = (detail) => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(API_NOTICE_EVENT, { detail }));
  }
};

const shouldSendCredentials = (requestUrl) => {
  const url = String(requestUrl || '');
  return (
    url.includes('/auth/')
    || url.includes('/portfolios')
    || url.includes('/planning')
    || url.includes('/admin/')
    || url.includes('/watchlist')
    || url.includes('/alerts')
    || url.includes('/goals')
    || url.includes('/portfolio-performance')
    || url.includes('/dashboard-performance')
  );
};

const isLongRunningRequest = (requestUrl) => /\/portfolio\/(optimize-weights|efficient-frontier|historical-performance|benchmark-analytics|backtest|stress-test|risk-decomposition|drift-monitor|style-analysis|brinson-attribution)|\/dashboard-performance/.test(String(requestUrl || ''));

const buildApiNotice = (error) => {
  if (error?.response?.status === 401) {
    return null;
  }

  if (error?.response?.status === 429) {
    return {
      level: 'info',
      health: 'degraded',
      messageKey: 'api_notice_rate_limit',
      message: 'We’re processing a lot of requests right now. Please wait about 30–60 seconds and try again.',
    };
  }

  if (error?.code === 'ECONNABORTED' || /timeout/i.test(String(error?.message || ''))) {
    return {
      level: 'warning',
      health: 'degraded',
      messageKey: 'api_notice_timeout',
      message: 'The live data service is taking longer than usual. Please wait about 30–90 seconds and try again — your session and saved portfolios are still safe.',
    };
  }

  if (!error?.response || error?.message === 'Network Error' || [502, 503, 504].includes(error?.response?.status)) {
    return {
      level: 'warning',
      health: 'error',
      messageKey: 'api_notice_reconnect',
      message: 'We’re having trouble reaching the analytics service right now. Please wait about 30–90 seconds while it reconnects, then try again.',
    };
  }

  if ((error?.response?.status || 0) >= 500) {
    return {
      level: 'warning',
      health: 'degraded',
      messageKey: 'api_notice_busy',
      message: 'The analytics engine is temporarily busy. Please wait about 30–90 seconds and retry your request.',
    };
  }

  return null;
};

const getCookieValue = (name) => {
  const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

axios.defaults.withCredentials = false;
axios.defaults.timeout = DEFAULT_API_TIMEOUT_MS;

axios.interceptors.request.use((config) => {
  const requestUrl = String(config?.url || '');
  if (shouldSendCredentials(requestUrl)) {
    config.withCredentials = true;
  } else if (typeof config.withCredentials === 'undefined') {
    // Public market/portfolio compute endpoints should avoid ambient session cookies.
    config.withCredentials = false;
  }

  if (typeof config.timeout === 'undefined') {
    config.timeout = isLongRunningRequest(requestUrl) ? LONG_RUNNING_API_TIMEOUT_MS : DEFAULT_API_TIMEOUT_MS;
  }

  const method = String(config?.method || 'get').toUpperCase();
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    const csrfToken = getCookieValue('portfolio_csrf_token');
    if (csrfToken) {
      config.headers = config.headers || {};
      config.headers['X-CSRF-Token'] = csrfToken;
    }
  }
  return config;
});

axios.interceptors.response.use(
  (response) => {
    emitApiNotice({ clear: true, health: 'healthy' });
    return response;
  },
  (error) => {
    const notice = buildApiNotice(error);
    if (notice) {
      emitApiNotice(notice);
    }

    if (error.response?.status === 401) {
      localStorage.removeItem('authUser');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

function AppContent() {
  const [, setApiHealth] = useState('checking');
  const [apiNotice, setApiNotice] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [planningNavOpen, setPlanningNavOpen] = useState(true);
  const [advancedNavOpen, setAdvancedNavOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [authUser, setAuthUser] = useState(null);
  const [guestMode, setGuestMode] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const accountMenuRef = useRef(null);
  const accountMenuCloseTimerRef = useRef(null);
  const location = useLocation();
  const theme = useThemeStore(state => state.theme);
  const { lang, setLang, t, tt } = useLanguage();

  const clearAccountMenuCloseTimer = () => {
    if (accountMenuCloseTimerRef.current) {
      window.clearTimeout(accountMenuCloseTimerRef.current);
      accountMenuCloseTimerRef.current = null;
    }
  };

  const openAccountMenu = () => {
    clearAccountMenuCloseTimer();
    setAccountMenuOpen(true);
  };

  const queueAccountMenuClose = () => {
    clearAccountMenuCloseTimer();
    accountMenuCloseTimerRef.current = window.setTimeout(() => {
      setAccountMenuOpen(false);
      accountMenuCloseTimerRef.current = null;
    }, 180);
  };

  // Initialize theme on mount
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    const handleApiNotice = (event) => {
      const detail = event?.detail || {};
      if (detail.clear) {
        setApiNotice(null);
      } else if (detail.message) {
        setApiNotice(detail);
      }

      if (detail.health) {
        setApiHealth(detail.health);
      }
    };

    window.addEventListener(API_NOTICE_EVENT, handleApiNotice);
    return () => window.removeEventListener(API_NOTICE_EVENT, handleApiNotice);
  }, []);

  useEffect(() => {
    const healthUrl = API_BASE.replace(/\/api$/, '') + '/health';
    let cancelled = false;

    const runHealthCheck = async () => {
      try {
        await axios.get(healthUrl, { timeout: 5000 });
        if (!cancelled) {
          setApiHealth('healthy');
          setApiNotice(null);
        }
      } catch (err) {
        console.warn('API health check failed:', err.message);
        if (!cancelled) {
          setApiHealth((prev) => (prev === 'healthy' ? 'degraded' : 'error'));
          setApiNotice({
            level: 'warning',
            messageKey: 'api_notice_health_degraded',
            message: 'Some live data features are temporarily delayed. Please wait about 30–90 seconds while the service reconnects, then try again.',
          });
        }
      }
    };

    runHealthCheck();
    const intervalId = window.setInterval(runHealthCheck, 45000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    const loadUser = async () => {
      // Check auth credentials BEFORE guestMode so logging in clears guest state
      const stored = localStorage.getItem('authUser');
      const csrfCookie = getCookieValue('portfolio_csrf_token');

      if (stored || csrfCookie) {
        try {
          const response = await axios.get(`${API_BASE}/auth/me`, { timeout: 30000, withCredentials: true });
          const user = response?.data;
          if (user?.email) {
            setAuthUser(user);
            setGuestMode(false);
            localStorage.setItem('authUser', JSON.stringify(user));
            localStorage.removeItem('guestMode');
            return;
          }
        } catch (_err) {
          // Only clear the session on a definitive 401 (invalid/expired token).
          // Do NOT clear on timeout or network errors — that would falsely log out
          // users when the Render free-tier backend is cold-starting.
          if (_err?.response?.status === 401) {
            localStorage.removeItem('authUser');
          }
        }
      }

      // No valid auth — check guest mode
      const guest = localStorage.getItem('guestMode') === 'true';
      setGuestMode(guest);
      setAuthUser(null);
    };

    loadUser();
    window.addEventListener('storage', loadUser);
    return () => window.removeEventListener('storage', loadUser);
  }, []);

  useEffect(() => {
    setMobileNavOpen(false);
    setAccountMenuOpen(false);
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target)) {
        clearAccountMenuCloseTimer();
        setAccountMenuOpen(false);
      }
    };

    window.addEventListener('pointerdown', handlePointerDown);
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
      clearAccountMenuCloseTimer();
    };
  }, []);

  const handleSignOut = async () => {
    clearAccountMenuCloseTimer();
    setAccountMenuOpen(false);
    try {
      await axios.post(`${API_BASE}/auth/logout`, {}, { timeout: 5000, withCredentials: true });
    } catch (err) {
      console.warn('Logout request failed:', err.message);
    }

    localStorage.removeItem('authUser');
    localStorage.removeItem('guestMode');
    setAuthUser(null);
    setGuestMode(false);
    window.location.href = '/';
  };

  const handleGuestLogin = () => {
    clearAccountMenuCloseTimer();
    setAccountMenuOpen(false);
    localStorage.removeItem('guestMode');
    localStorage.removeItem('authUser');
    setGuestMode(false);
    setAuthUser(null);
    window.location.href = '/login';
  };

  const handleChangePassword = () => {
    clearAccountMenuCloseTimer();
    setAccountMenuOpen(false);
    window.location.assign('/change-password');
  };

  const planningMenuItems = [
    { path: '/planning', label: t('nav_start_here', 'Start Here'), icon: FiActivity },
    { path: '/planning/accounts', label: t('nav_learn_accounts', 'Learn Accounts'), icon: FiHelpCircle },
    { path: '/planning/concepts', label: t('nav_learn_investing', 'Learn Investing'), icon: FiInfo },
    { path: '/planning/goals', label: t('nav_set_goal', 'Set a Goal'), icon: FiCrosshair },
    { path: '/planning/forecast', label: t('nav_forecast_growth', 'Forecast Growth'), icon: FiTrendingUp },
    { path: '/planning/retirement', label: t('nav_retirement_planning', 'Retirement Planning'), icon: FiShield },
  ];

  const advancedMenuItems = [
    { path: '/', label: t('nav_portfolio_home', 'Portfolio Home'), icon: FiGrid },
    { path: '/portfolio-builder', label: t('nav_builder', 'Portfolio Builder'), icon: FiBriefcase },
    { path: '/portfolio', label: t('nav_optimizer', 'Optimizer'), icon: FiGrid },
    { path: '/market-data', label: t('nav_asset_research', 'Asset Research'), icon: FiBarChart2 },
    { path: '/performance', label: t('nav_portfolio_analytics', 'Portfolio Analytics'), icon: FiTrendingUp },
    { path: '/compare-stocks', label: t('nav_stock_comparison', 'Stock Comparison'), icon: RiScales3Line },
  ];

  const footerLinks = [
    { path: '/contact', label: t('nav_contact', 'Contact'), icon: FiMail },
    { path: '/help', label: t('nav_help', 'Help'), icon: FiHelpCircle },
    { path: '/planning/mission', label: t('nav_mission', 'Mission'), icon: FiTarget },
  ];

  const isPlanningWorkspace = location.pathname.startsWith('/planning');
  const currentWorkspaceItems = [...planningMenuItems, ...advancedMenuItems];
  const workspaceSwitch = isPlanningWorkspace
    ? { path: '/portfolio-builder', label: t('workspace_open_optimization', 'Open Portfolio Optimization'), icon: FiBriefcase }
    : { path: '/planning', label: t('workspace_open_planning', 'Open Financial Planning Hub'), icon: FiActivity };
  const workspaceTitle = isPlanningWorkspace
    ? t('workspace_planning_title', 'Financial Planning Workspace')
    : t('workspace_optimization_title', 'Portfolio Optimization Workspace');
  const workspaceSubtitle = t('sidebar_tagline', 'Canadian. Independent. Focused on you.');

  const startPlanning = (targetPath = '/planning') => {
    localStorage.setItem('guestMode', 'true');
    setGuestMode(true);
    if (!localStorage.getItem('onboardingComplete')) {
      setShowOnboarding(true);
    }
    window.location.href = targetPath;
  };

  const openOptimization = () => {
    localStorage.setItem('guestMode', 'true');
    setGuestMode(true);
    if (!localStorage.getItem('onboardingComplete')) {
      setShowOnboarding(true);
    }
    window.location.href = '/portfolio-builder';
  };

  const openLogin = () => {
    window.location.href = '/login';
  };

  const shellTitle = isPlanningWorkspace
    ? t('topbar_planning_hub', 'Financial Planning Hub')
    : t('topbar_portfolio_toolkit', 'Portfolio Toolkit');
  const staticPlanningRoutes = ['/planning', '/planning/accounts', '/planning/concepts', '/planning/mission'];
  const showApiNotice = Boolean(apiNotice?.message) && !staticPlanningRoutes.includes(location.pathname);

  if (!authUser && !guestMode) {
    // Allow /login path through so Sign In works
    if (location.pathname === '/login') {
      return <Login apiBase={API_BASE} fullScreen />;
    }
    return (
      <Landing
        onStartPlanning={startPlanning}
        onOpenOptimization={openOptimization}
        onLogin={openLogin}
      />
    );
  }

  const canAccessAdmin = Boolean(authUser?.is_admin);
  const accountLabel = authUser?.email || tt('Account');

  const NavLinks = ({ onNavigate, isMobile = false }) => {
    const showGroupToggles = isMobile || !sidebarCollapsed;
    const isNavItemActive = (path) => location.pathname === path || (path !== '/' && location.pathname.startsWith(`${path}/`));
    const renderNavItem = (item, keyPrefix = '') => {
      const Icon = item.icon;
      const isActive = isNavItemActive(item.path);
      return (
        <Link
          key={`${keyPrefix}${item.path}`}
          to={item.path}
          className={`shell-nav-link ${isActive ? 'active' : ''}`}
          onClick={onNavigate}
        >
          <Icon className="shell-nav-icon" />
          {(isMobile || !sidebarCollapsed) && <span>{item.label}</span>}
        </Link>
      );
    };

    if (!showGroupToggles) {
      return (
        <nav className="shell-nav-links">
          {currentWorkspaceItems.map((item) => renderNavItem(item, 'collapsed-'))}
        </nav>
      );
    }

    return (
      <nav className="shell-nav-links">
        <div className={`shell-nav-group ${planningNavOpen ? 'open' : ''}`}>
          <button
            type="button"
            className="shell-nav-group-toggle"
            onClick={() => setPlanningNavOpen((prev) => !prev)}
            aria-expanded={planningNavOpen}
          >
            <span>{t('nav_group_planning', 'Financial Planning')}</span>
            <FiArrowRight className="shell-nav-group-arrow" />
          </button>
          {planningNavOpen && (
            <div className="shell-nav-group-items">
              {planningMenuItems.map((item) => renderNavItem(item, 'planning-'))}
            </div>
          )}
        </div>

        <div className={`shell-nav-group ${advancedNavOpen ? 'open' : ''}`}>
          <button
            type="button"
            className="shell-nav-group-toggle"
            onClick={() => setAdvancedNavOpen((prev) => !prev)}
            aria-expanded={advancedNavOpen}
          >
            <span>{t('nav_group_advanced_tools', 'Advanced Tools')}</span>
            <FiArrowRight className="shell-nav-group-arrow" />
          </button>
          {advancedNavOpen && (
            <div className="shell-nav-group-items">
              {advancedMenuItems.map((item) => renderNavItem(item, 'advanced-'))}
            </div>
          )}
        </div>
      </nav>
    );
  };

  return (
    <div className="shell-app">
      <a href="#main-content" className="skip-to-main">{tt('Skip to main content')}</a>
      <KeyboardShortcuts />
      {showOnboarding && <OnboardingWizard onClose={() => setShowOnboarding(false)} />}
      <button
        className="shell-mobile-toggle"
        onClick={() => setMobileNavOpen(true)}
        aria-label={tt('Open navigation')}
      >
        <FiMenu />
      </button>

      {mobileNavOpen && <div className="shell-mobile-overlay" onClick={() => setMobileNavOpen(false)} />}

      <aside className={`shell-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="shell-sidebar-header">
          <Link to="/landing" className="shell-brand" onClick={() => setMobileNavOpen(false)}>
            <img src={logoPlaceholder} alt={tt('Logo')} className="shell-logo-image" />
            {!sidebarCollapsed && (
              <div className="shell-brand-text">
                <strong>{tt('Hamilton Services')}</strong>
              </div>
            )}
          </Link>
          <div className="shell-header-actions">
            <button
              className="shell-icon-btn desktop"
              onClick={() => setSidebarCollapsed((prev) => !prev)}
              aria-label={tt('Toggle desktop sidebar')}
            >
              <FiMenu />
            </button>
            <button
              className="shell-icon-btn mobile"
              onClick={() => setMobileNavOpen(false)}
              aria-label={tt('Close mobile navigation')}
            >
              <FiX />
            </button>
          </div>
        </div>

        <div className="shell-workspace-card">
          {!sidebarCollapsed && (
            <>
              <p className="shell-workspace-title">{workspaceTitle}</p>
              <p className="shell-workspace-subtitle">{workspaceSubtitle}</p>
            </>
          )}
          <Link to={workspaceSwitch.path} className="shell-workspace-link" onClick={() => setMobileNavOpen(false)}>
            <workspaceSwitch.icon className="shell-nav-icon" />
            {!sidebarCollapsed && <span>{workspaceSwitch.label}</span>}
            {!sidebarCollapsed && <FiArrowRight className="shell-workspace-arrow" />}
          </Link>
        </div>

        <NavLinks onNavigate={() => setMobileNavOpen(false)} />

        <div className="shell-sidebar-footer">
          {footerLinks.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`shell-nav-link shell-footer-link ${isActive ? 'active' : ''}`}
                onClick={() => setMobileNavOpen(false)}
              >
                <Icon />
                {!sidebarCollapsed && <span>{item.label}</span>}
              </Link>
            );
          })}
        </div>
      </aside>

      <aside className={`shell-sidebar-mobile ${mobileNavOpen ? 'open' : ''}`}>
        <div className="shell-sidebar-header">
          <Link to="/landing" className="shell-brand" onClick={() => setMobileNavOpen(false)}>
            <img src={logoPlaceholder} alt={tt('Logo')} className="shell-logo-image" />
            <div className="shell-brand-text">
              <strong>{tt('Hamilton Services')}</strong>
            </div>
          </Link>
          <button
            className="shell-icon-btn mobile"
            onClick={() => setMobileNavOpen(false)}
            aria-label={tt('Close mobile navigation')}
          >
            <FiX />
          </button>
        </div>

        <div className="shell-workspace-card">
          <p className="shell-workspace-title">{workspaceTitle}</p>
          <p className="shell-workspace-subtitle">{workspaceSubtitle}</p>
          <Link to={workspaceSwitch.path} className="shell-workspace-link" onClick={() => setMobileNavOpen(false)}>
            <workspaceSwitch.icon className="shell-nav-icon" />
            <span>{workspaceSwitch.label}</span>
            <FiArrowRight className="shell-workspace-arrow" />
          </Link>
        </div>

        <NavLinks onNavigate={() => setMobileNavOpen(false)} isMobile />

        <div className="shell-sidebar-footer">
          {footerLinks.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={`mobile-${item.path}`}
                to={item.path}
                className={`shell-nav-link shell-footer-link ${isActive ? 'active' : ''}`}
                onClick={() => setMobileNavOpen(false)}
              >
                <Icon />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </div>
      </aside>

      <main id="main-content" className={`shell-main ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <header className="shell-topbar">
          <h1>{shellTitle}</h1>
          <div className="shell-topbar-actions">
            <button
              type="button"
              className="shell-topbar-utility-btn"
              onClick={() => setLang(lang === 'en' ? 'fr' : 'en')}
              title={lang === 'en' ? tt('Switch to French') : tt('Switch to English')}
            >
              <FaCanadianMapleLeaf aria-hidden="true" />
              <span>{tt('Canada')}</span>
              <small>{lang === 'en' ? 'FR' : 'EN'}</small>
            </button>
            <div
              className="shell-topbar-account-wrap"
              ref={accountMenuRef}
              onMouseEnter={openAccountMenu}
              onMouseLeave={queueAccountMenuClose}
            >
              <button
                type="button"
                className={`shell-topbar-account-btn ${accountMenuOpen ? 'active' : ''}`}
                onClick={() => {
                  clearAccountMenuCloseTimer();
                  setAccountMenuOpen((prev) => !prev);
                }}
                title={accountLabel}
                aria-haspopup="menu"
                aria-expanded={accountMenuOpen}
              >
                <FiUser />
                <span>{accountLabel}</span>
              </button>
              <div
                className={`shell-account-menu ${accountMenuOpen ? 'open' : ''}`}
                role="menu"
                aria-label={tt('Account')}
                onMouseEnter={openAccountMenu}
                onMouseLeave={queueAccountMenuClose}
              >
                {authUser ? (
                  <>
                    <button type="button" className="shell-account-menu-item" onClick={handleChangePassword}>
                      <FiSettings />
                      <span>{tt('Change Password')}</span>
                    </button>
                    <button type="button" className="shell-account-menu-item danger" onClick={handleSignOut}>
                      <FiLogOut />
                      <span>{t('nav_sign_out', 'Sign out')}</span>
                    </button>
                  </>
                ) : (
                  <button type="button" className="shell-account-menu-item" onClick={handleGuestLogin}>
                    <FiLogIn />
                    <span>{t('nav_sign_in', 'Sign in')}</span>
                  </button>
                )}
              </div>
            </div>
            {canAccessAdmin && (
              <button
                className="shell-icon-btn"
                onClick={() => window.location.assign('/admin')}
                aria-label={tt('Open settings')}
              >
                <FiSettings />
              </button>
            )}
          </div>
        </header>
        {showApiNotice && (
          <div className={`shell-api-banner ${apiNotice.level || 'warning'}`} role="status" aria-live="polite">
            <strong className="shell-api-banner-title">{tt('Live service notice')}</strong>
            <span>{apiNotice.messageKey ? t(apiNotice.messageKey, apiNotice.message) : tt(apiNotice.message)}</span>
          </div>
        )}
        <div className="shell-content">
          <Routes>
            <Route path="/" element={<Dashboard apiBase={API_BASE} />} />
            <Route path="/landing" element={<Landing onStartPlanning={startPlanning} onOpenOptimization={openOptimization} onLogin={openLogin} />} />
            <Route path="/planning" element={<FinancialPlanningHub />} />
            <Route path="/planning/mission" element={<PlanningMission />} />
            <Route path="/planning/accounts" element={<AccountsLearningCenter />} />
            <Route path="/planning/concepts" element={<ConceptsLibrary />} />
            <Route path="/planning/forecast" element={<GrowthForecastCalculator apiBase={API_BASE} authUser={authUser} />} />
            <Route path="/planning/goals" element={<GoalPlanningEngine apiBase={API_BASE} authUser={authUser} />} />
            <Route path="/planning/retirement" element={<RetirementReadinessCenter apiBase={API_BASE} authUser={authUser} />} />
            <Route path="/portfolio" element={<PortfolioGenerator apiBase={API_BASE} />} />
            <Route path="/portfolio-builder" element={<PortfolioBuilder apiBase={API_BASE} />} />
            <Route path="/market-data" element={<MarketData apiBase={API_BASE} />} />
            <Route path="/compare" element={<Navigate to="/performance" replace />} />
            <Route path="/compare-stocks" element={<StockComparison apiBase={API_BASE} />} />
            <Route path="/watchlist" element={authUser ? <Watchlist apiBase={API_BASE} /> : <Navigate to="/login" replace />} />
            <Route path="/alerts" element={authUser ? <Alerts apiBase={API_BASE} /> : <Navigate to="/login" replace />} />
            <Route path="/performance" element={authUser ? <PerformanceDashboard apiBase={API_BASE} /> : <Navigate to="/login" replace />} />
            <Route path="/goals" element={authUser ? <Goals apiBase={API_BASE} /> : <Navigate to="/login" replace />} />
            <Route path="/help" element={<HelpDocs />} />
            <Route path="/shared/:shareToken" element={<SharedPortfolio apiBase={API_BASE} />} />
            <Route path="/mission" element={<Mission />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/change-password" element={authUser ? <ChangePassword apiBase={API_BASE} /> : <Navigate to="/login" replace />} />
            <Route path="/admin" element={canAccessAdmin ? <AdminPanel apiBase={API_BASE} /> : <Navigate to="/" replace />} />
            <Route path="/login" element={<Login apiBase={API_BASE} />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <Router>
        <AppContent />
      </Router>
    </LanguageProvider>
  );
}

export default App;
